/**
 * 界面「线框样式」登记表（线框强度的唯一数据源）。
 *
 * 为什么做成可配：控件边界（输入框/下拉/日期）从"几乎看不见"提到可识别的 3:1 后，
 * 视觉重量明显变重——有人要照旧的低干扰观感，也有人要更强的边界（低质显示器 / 弱视 / 远距看板）。
 * 两档诉求都在真实项目里存在，故把强度做成用户可选，而**不是**替所有人定死。
 *
 * 三档语义（`applyTheme.ts` 是唯一下发点，本文件只存"参数"）：
 * - `soft`   ：贴近"无边框"的极简观感（控件描边走主色 alpha 档，合成对比 ≈1.4:1）。**不满足 WCAG 1.4.11**
 *              （该条要求可交互控件边界 ≥3:1），属用户显式选择的观感优先档。
 * - `standard`：控件边界按当前主题的中性文字色派生到 ≈3:1（默认档，满足 1.4.11）。
 * - `strong` ：≈4.3:1，边界更硬朗，适合弱视 / 低质显示器 / 投屏。
 * 装饰性描边（卡片、表格、弹窗）不承担可用性职责，三档只做轻重区分，不设 3:1 要求。
 */

export type BorderLevelKey = 'soft' | 'standard' | 'strong';

/** 控件描边取值方式：alpha = 主色透明档（含主题色温）；contrast = 按中性文字色派生到指定对比度 */
export type BorderToneSpec = { kind: 'alpha'; alpha: number } | { kind: 'contrast'; target: number };

export interface BorderLevelDef {
  key: BorderLevelKey;
  name: string;
  /** 抽屉内一句话说明（用户可见，只讲清观感与可达性，不写内部取舍） */
  desc: string;
  /** 可交互控件边界（输入框 / 下拉 / 日期 / 表单三态） */
  control: BorderToneSpec;
  /** 控件 hover 态 */
  controlHover: BorderToneSpec;
  /** 装饰性描边：卡片表格描边与其 hover 档 */
  border: number;
  borderStrong: number;
  /** Arco 结构描边（卡片/表格/弹窗/抽屉/分隔线）与其 hover 档 */
  colorBorder2: number;
  colorBorder3: number;
}

export const BORDER_LEVELS: BorderLevelDef[] = [
  {
    key: 'soft',
    name: '轻',
    desc: '边界最轻：界面更干净、干扰更小，但对弱视或低质显示器不够清晰',
    control: { kind: 'alpha', alpha: 0.14 },
    controlHover: { kind: 'alpha', alpha: 0.22 },
    border: 0.16,
    borderStrong: 0.26,
    colorBorder2: 0.14,
    colorBorder3: 0.22,
  },
  {
    key: 'standard',
    name: '标准',
    desc: '边界清晰可辨（推荐）：满足无障碍对比度要求',
    control: { kind: 'contrast', target: 3 },
    controlHover: { kind: 'contrast', target: 4.3 },
    border: 0.24,
    borderStrong: 0.38,
    colorBorder2: 0.22,
    colorBorder3: 0.34,
  },
  {
    key: 'strong',
    name: '强调',
    desc: '边界更硬朗：适合弱视、低质显示器与远距投屏',
    control: { kind: 'contrast', target: 4.3 },
    controlHover: { kind: 'contrast', target: 5.5 },
    border: 0.38,
    borderStrong: 0.5,
    colorBorder2: 0.34,
    colorBorder3: 0.5,
  },
];

/**
 * 默认档 = `soft`（观感优先）：产品侧确认以"低干扰极简"为默认观感。
 * 交付方若需要默认即满足 WCAG 1.4.11（可交互控件边界 ≥3:1），把这里改成 `standard` 即可，
 * 其余代码与设置面板会跟着走（设置面板无论默认是哪一档，三档都可选）。
 */
export const DEFAULT_BORDER_LEVEL: BorderLevelKey = 'soft';

/** 按 key 取档位（未知 key 回退默认档，避免旧版本存储键把界面锁死） */
export function getBorderLevel(key: string | null | undefined): BorderLevelDef {
  // 回退目标的唯一来源是 DEFAULT_BORDER_LEVEL：不要写成固定下标，否则改默认档时存储空值会落到另一档
  return (
    BORDER_LEVELS.find((b) => b.key === key) ??
    BORDER_LEVELS.find((b) => b.key === DEFAULT_BORDER_LEVEL) ??
    BORDER_LEVELS[0]
  );
}
